import styled from "@emotion/styled"
import {
  headerSearchClosed,
  headerSearchInput,
  headerSearchOpened,
  searchAbandoned,
} from "@eurodrive/utils/gtm"
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { SaleCarDtoPaginatedList, SearchMetadataResponse } from "api"
import debounce from "debounce"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useCallback, useEffect, useState } from "react"

import { transformCarFeatures, transformCarTypes } from "@eurodrive/utils"
import { sharedColors } from "../../../theme/colors"
import { mediaQueries } from "../../../theme/mediaQueries"
import Results from "./Results"
import { fetchSearchResults } from "./utils"

const StyledContainer = styled.div`
  position: relative;

  ${mediaQueries.mobile} {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1300;
  }
`

const StyledModalBox = styled.div<{ hasBottomRadius: boolean }>`
  border-radius: 0px 0px 8px 8px;
  position: absolute;
  top: 72px;
  left: 50%;
  transform: translateX(-50%);
  width: 682px;
  height: 442px;
  background-color: white;
  margin: 0 auto;
  display: flex;
  flex-direction: column;

  ${mediaQueries.mobile} {
    position: fixed;
    top: 0;
    left: 0;
    transform: none;
    width: 100%;
    height: 100vh;
    border-radius: 0;
    z-index: 1300;
  }
`

const SearchBox = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  gap: 8px;

  ${mediaQueries.mobile} {
    padding: 16px 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
`

const Divider = styled(Box)`
  width: 1px;
  height: 12px;
  background-color: #6f6f6f;
  margin-inline-end: 8px;
  margin-inline-start: 8px;
`

const StyledIconButton = styled(IconButton)`
  padding: 4px;
  margin: 0;
  min-width: 12px;
  width: 12px;
  height: 12px;
  border-radius: 4px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.08);
  }

  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`
const ResultsBox = styled.div`
  flex: 1;
  padding: 24px 20px 0 20px;

  ${mediaQueries.mobile} {
    flex: 1;
    padding: 24px 20px 24px 20px;
    overflow-y: auto;
  }
`

const StyledTextButton = styled(Button)`
  width: fit-content;
  min-width: 0;
  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`
const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 8px;
    background-color: ${sharedColors.white};
    border: 1px solid rgba(0, 0, 0, 0.2);
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 4px;

    &:hover {
      border-color: rgba(0, 0, 0, 0.3);
    }

    &.Mui-focused {
      border-color: ${sharedColors.darkGreen};
      box-shadow: 0 0 0 1px ${sharedColors.darkGreen};
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    .MuiInputAdornment-root {
      margin: 0;
    }
    .MuiInputBase-input {
      font-size: 16px;
      font-weight: 400;
      line-height: 1.4;
      color: ${sharedColors.black};
      padding: 0;
      flex: 1;

      &::placeholder {
        color: rgba(0, 0, 0, 0.2);
        opacity: 1;
      }
    }

    ${mediaQueries.mobile} {
      padding: 8px 12px;
      border: 1px solid ${sharedColors.black};

      &:hover {
        border-color: ${sharedColors.black};
      }

      &.Mui-focused {
        border-color: ${sharedColors.black};
        box-shadow: 0 0 0 1px ${sharedColors.black};
      }
    }
  }
`
const SearchIconContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-inline-end: 4px;
`

const CloseIconContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-inline-start: 4px;
`

type Props = {
  open: boolean
  handleClose: () => void
  searchCars: SaleCarDtoPaginatedList
  metadata: SearchMetadataResponse
}

export const SearchModal = ({
  open,
  handleClose,
  searchCars,
  metadata,
}: Props) => {
  const [text, setText] = useState<string>("")
  const [debouncedText, setDebouncedText] = useState<string>("")
  const pathname = usePathname()

  // Track when modal opens
  useEffect(() => {
    if (open) {
      headerSearchOpened(pathname)
    }
  }, [open, pathname])

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["search", debouncedText],
    queryFn: () => fetchSearchResults(debouncedText),
    enabled: !!debouncedText,
    refetchOnWindowFocus: false,
  })
  const close = () => {
    // Track search abandonment if user typed something but is leaving without results
    if (debouncedText.length > 0) {
      const hasResults =
        data &&
        (data.searchItems?.length > 0 ||
          data.leasingCars?.length > 0 ||
          data.saleCars?.length > 0)

      if (!hasResults) {
        searchAbandoned(debouncedText, "no_results")
      }
    } else if (text.length > 0) {
      // User typed but left before debounce completed
      searchAbandoned(text, "incomplete_search")
    }

    headerSearchClosed(pathname)
    handleClose()
    setText("")
    setDebouncedText("")
  }
  const handleClear = () => {
    setText("")
    setDebouncedText("")
  }
  const debouncedSetText = useCallback(
    debounce((value: string) => {
      setDebouncedText(value)
    }, 500),
    [],
  )
  useEffect(() => {
    close()
  }, [pathname])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setText(value) // Immediate UI update
    debouncedSetText(value) // Debounced API trigger

    // Track search input
    if (value.length > 0) {
      headerSearchInput(value, String(value.length))
    }
  }
  return (
    <Modal
      open={open}
      onClose={close}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "transparent",
            "@media (max-width: 768px)": {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      }}
    >
      <StyledContainer>
        <StyledModalBox hasBottomRadius={!!debouncedText}>
          <SearchBox>
            <StyledTextField
              value={text}
              onChange={handleInputChange}
              placeholder={"איזה רכב נוכל למצוא עבורך?"}
              variant="outlined"
              fullWidth
              autoComplete="search"
              aria-label="חיפוש רכבים"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="start">
                      <SearchIconContainer>
                        <Image
                          src="/icons/search-icon.svg"
                          alt=""
                          width={16}
                          height={16}
                          style={{
                            filter: `brightness(0) saturate(100%) invert(13%) sepia(28%) saturate(2749%) hue-rotate(93deg) brightness(94%) contrast(97%)`, // Convert to #194F20
                          }}
                        />
                      </SearchIconContainer>
                    </InputAdornment>
                  ),
                  startAdornment: text && (
                    <InputAdornment position="end">
                      <CloseIconContainer>
                        <StyledIconButton
                          onClick={handleClear}
                          aria-label="נקה חיפוש"
                          size="small"
                        >
                          <Image
                            src="/icons/close-icon.svg"
                            alt=""
                            width={12}
                            height={12}
                            style={{
                              filter: `brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)`, // Convert to black
                            }}
                          />
                        </StyledIconButton>
                      </CloseIconContainer>
                      <Divider />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <StyledTextButton variant="text" onClick={close}>
              <Typography variant="body3">יציאה</Typography>
            </StyledTextButton>
          </SearchBox>
          <Results
            handleClose={handleClose}
            results={data}
            searchCars={searchCars}
            carFeatures={transformCarFeatures(metadata.tags)}
            carTypes={transformCarTypes(metadata.types)}
            isSearching={!!debouncedText && (isLoading || isFetching)}
          />
        </StyledModalBox>
      </StyledContainer>
    </Modal>
  )
}
